import { useAgentCommandService } from '@/services/agent/AgentCommandService'
import { storage } from '@/firebase/config'
import { ref as storageRef, getDownloadURL } from 'firebase/storage'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@/firebase/config'
import type { RpcCommand, ScaleReading } from '@/types/Agent'
type Session = { tenantId?: string; yardId?: string; userId?: string }

type WaitResult = {
  status: RpcCommand['status']
  result?: unknown
  error?: string
}

export async function waitForCommand(
  session: Session,
  agentId: string,
  commandId: string,
  timeoutMs: number,
): Promise<WaitResult> {
  const agentCommandService = useAgentCommandService(session)
  const t0 = Date.now()
  console.log('[commands] waitForCommand: start', { agentId, commandId, timeoutMs })

  // Quick check in case it already completed
  const initial = await agentCommandService.getCommand(agentId, commandId)
  if (initial) {
    if (initial.status === 'succeeded' || initial.status === 'failed') {
      console.log('[commands] waitForCommand: initial terminal state', {
        commandId,
        status: initial.status,
        elapsedMs: Date.now() - t0,
      })
      return { status: initial.status, result: initial.result, error: initial.error?.message }
    }
    console.log('[commands] waitForCommand: initial status', {
      commandId,
      status: initial.status,
      elapsedMs: Date.now() - t0,
    })
  }

  return new Promise<WaitResult>((resolve, _reject) => {
    let settled = false

    const ref = doc(db, `tenants/${session.tenantId}/agents/${agentId}/rpc/${commandId}`)
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return
      const command = snap.data() as any

      console.log('[commands] onSnapshot(doc)', {
        commandId,
        status: command.status,
        elapsedMs: Date.now() - t0,
      })

      if (command.status === 'succeeded') {
        if (!settled) {
          settled = true
          unsub()
          resolve({ status: 'succeeded', result: command.result })
        }
      } else if (command.status === 'failed') {
        if (!settled) {
          settled = true
          unsub()
          resolve({ status: 'failed', error: command.error?.message })
        }
      }
    })

    const _to = setTimeout(() => {
      if (!settled) {
        settled = true
        try {
          unsub()
        } catch {}
        resolve({ status: 'failed', error: 'timeout' })
      }
    }, timeoutMs)
  })
}

export async function readScaleAndWait(
  session: Session,
  agentId: string,
  scaleId: string,
  timeoutMs: number = 10_000,
): Promise<ScaleReading> {
  const agentCommandService = useAgentCommandService(session)
  const commandId = await agentCommandService.sendCommand(agentId, 'scale.read', { scaleId })
  const res = await waitForCommand(session, agentId, commandId, timeoutMs)
  if (res.status !== 'succeeded') {
    throw new Error(res.error || 'Scale read failed')
  }
  const payload = (res.result ?? {}) as { reading?: ScaleReading }
  if (!payload.reading) throw new Error('Scale read missing reading')
  return payload.reading
}

export async function captureCameraAndWait(
  session: Session,
  agentId: string,
  cameraId: string,
  mode: string = 'http',
  timeoutMs: number = 30_000,
): Promise<{ url: string; storagePath: string }> {
  const agentCommandService = useAgentCommandService(session)
  const commandId = await agentCommandService.sendCommand(agentId, 'camera.capture', {
    cameraId,
    mode,
  })
  const res = await waitForCommand(session, agentId, commandId, timeoutMs)
  if (res.status !== 'succeeded') {
    throw new Error(res.error || 'Camera capture failed')
  }
  const payload = (res.result ?? {}) as { storagePath?: string }
  if (!payload.storagePath) throw new Error('Camera capture missing storagePath')
  const storagePath = payload.storagePath
  const ref = storageRef(storage, storagePath)
  const url = await getDownloadURL(ref)
  return { url, storagePath }
}

export async function scanIdAndWait(
  session: Session,
  agentId: string,
  options?: { scannerId?: string; timeoutMs?: number },
): Promise<{ text: string }> {
  const agentCommandService = useAgentCommandService(session)
  const commandId = await agentCommandService.sendCommand(agentId, 'id.scan', options || {})
  const res = await waitForCommand(
    session,
    agentId,
    commandId,
    Math.max(1_000, options?.timeoutMs ?? 30_000),
  )
  if (res.status !== 'succeeded') {
    throw new Error(res.error || 'ID scan failed')
  }
  // Extract text from result
  const text = ((res.result ?? {}) as { scan?: { text?: string } }).scan?.text || ''
  if (!text) {
    throw new Error('ID scan returned no text')
  }

  return { text }
}

export async function captureScaleWithImageAndWait(
  session: Session,
  agentId: string,
  scaleId: string,
  timeoutMs: number = 30_000,
): Promise<{ weight: ScaleReading; image: { url: string; storagePath: string } | null }> {
  const agentCommandService = useAgentCommandService(session)
  const commandId = await agentCommandService.sendCommand(agentId, 'captureScaleWithImage', {
    scaleId,
  })
  const res = await waitForCommand(session, agentId, commandId, timeoutMs)
  if (res.status !== 'succeeded') {
    throw new Error(res.error || 'Scale with image capture failed')
  }
  const payload = (res.result ?? {}) as {
    weight?: ScaleReading
    image?: { storagePath: string }
  }
  if (!payload.weight) throw new Error('Scale with image capture missing weight')

  let image: { url: string; storagePath: string } | null = null
  if (payload.image?.storagePath) {
    const ref = storageRef(storage, payload.image.storagePath)
    const url = await getDownloadURL(ref)
    image = { url, storagePath: payload.image.storagePath }
  }

  return { weight: payload.weight, image }
}
